import React, { Component } from "react";
import {
  AdminService,
  ExecutionEnvironment,
  ValueTupleOfStringAndMission,
} from "../../generated";
import moment from "moment";
import { Col, Container, Label, Row, Spinner } from "reactstrap";
import {
  IconButton,
  MenuItem,
  Select,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import {
  MyLocation,
  AccountBalance,
  Done,
  Payment,
  Clear,
  ZoomIn,
} from "@mui/icons-material";
import {
  ExecutionEnv,
  ProdRegionsExecutionEnvtTypes,
  SpiirExecutionEnvType,
  SpiirRegionsExecutionEnvTypes,
} from "../../generated/models/ExecutionEnvironment";
import { Area, areas } from "./Area";
import { Policy } from "../policy";

export interface State {
  isLoading: boolean;
  result: ValueTupleOfStringAndMission[];
  area: string;
}

const DEFAULT_AREA = areas.find((area) => area.value === "Uk")?.value;

export class AdminViewMissions extends Component<{}, State> {
  constructor(props: {}) {
    super(props);

    this.state = {
      isLoading: true,
      result: [],
      area: DEFAULT_AREA,
    };
  }

  async componentDidMount() {
    try {
      const result = await AdminService.adminGetMissions({
        onlyMine: true,
        area: DEFAULT_AREA,
      });
      this.setState({
        isLoading: false,
        result,
      });
    } catch (e: any) {
      this.setState({
        isLoading: false,
        result: [],
      });
    }
  }

  async getBanks(area: string) {
    try {
      this.setState({
        isLoading: true,
      });

      const result = await AdminService.adminGetMissions({
        onlyMine: true,
        area,
      });
      this.setState({
        isLoading: false,
        result,
      });
    } catch (e: any) {
      this.setState({
        isLoading: false,
        result: [],
      });
    }
  }

  handleAreaChange = async (event: SelectChangeEvent) => {
    this.setState({
      area: event.target.value as string,
    });
    await this.getBanks(event.target.value);
  };

  renderEmptyRow() {
    return (
      <TableRow>
        <TableCell colSpan={8} align="center">
          Nothing here
        </TableCell>
      </TableRow>
    );
  }

  displayExecEnvName(execEnv: ExecutionEnvironment) {
    return ExecutionEnv.concat(ProdRegionsExecutionEnvtTypes)
      .concat(SpiirExecutionEnvType)
      .concat(SpiirRegionsExecutionEnvTypes)
      .find((env) => env.id === execEnv).label;
  }

  renderRows(missions: ValueTupleOfStringAndMission[]) {
    return missions.map((tuple, index) => (
      <TableRow key={`mission-${index}`}>
        <TableCell>
          <Tooltip title={tuple.item2.type}>
            <>
              {tuple.item2.type === "Accounts" && <AccountBalance />}
              {tuple.item2.type === "Payments" && <Payment />}
              {tuple.item2.type === "BulkPayments" && (
                <>
                  <Payment />
                  <Payment />
                </>
              )}
            </>
          </Tooltip>
        </TableCell>
        <TableCell>
          {tuple.item2.providerId ||
            (tuple.item2.providers ? tuple.item2.providers.join(", ") : "")}
        </TableCell>
        <TableCell>
          {this.displayExecEnvName(tuple.item2.executionEnvironment)}
        </TableCell>
        <TableCell>
          {tuple.item2.testerEmail || tuple.item2.firstName}
        </TableCell>
        <TableCell>
          <>
            {tuple.item2.history.length > 0 &&
              tuple.item2.history[0].success && (
                <Done className={"text-success"} />
              )}
            {tuple.item2.history.length > 0 &&
              !tuple.item2.history[0].success && (
                <Clear className={"text-danger"} />
              )}
          </>
        </TableCell>
        <TableCell>
          {tuple.item2.createdAt && moment(tuple.item2.createdAt).fromNow()}
        </TableCell>
        <TableCell>
          <>
            <Tooltip title="Start mission">
              <IconButton
                onClick={() => {
                  window.open(`/start?code=${tuple.item1}`);
                }}
                color="primary"
              >
                <MyLocation />
              </IconButton>
            </Tooltip>
            <Tooltip title="Splunk logs">
              <IconButton
                color={"primary"}
                onClick={() => {
                  window.open(
                    `https://eu-mastercard.splunkcloud.com/en-GB/app/mc_openbanking_search/search?q=search%20%22fields.UserHash%22%3D"${tuple.item1}"&display.page.search.mode=smart&dispatch.sample_ratio=1&earliest=-7d%40d&latest=now`
                  );
                }}
              >
                <ZoomIn />
              </IconButton>
            </Tooltip>
          </>
        </TableCell>
      </TableRow>
    ));
  }

  renderTableBody(missions: ValueTupleOfStringAndMission[]) {
    return (
      <>
        {missions.length > 0
          ? this.renderRows(missions)
          : this.renderEmptyRow()}
      </>
    );
  }
  renderPrivacyNotice() {
    return (
      !this.state.isLoading &&
      this.state.result.length > 0 && <Policy area={this.state.area} />
    );
  }
  render() {
    return (
      <Container>
        <Row>
          <Col>
            <h3>Your missions</h3>
          </Col>
        </Row>
        <Row alignItems="center">
          <Col>
            <Label sx={{ marginRight: "800px" }}>Area:&nbsp;</Label>
            <Select
              defaultValue={DEFAULT_AREA}
              value={this.state.area}
              onChange={this.handleAreaChange}
            >
              {areas?.map((area: Area) => (
                <MenuItem key={area.id} value={area.value}>
                  {area.name}
                </MenuItem>
              ))}
            </Select>
          </Col>
        </Row>
        <Row>
          <Col>
            {this.state.isLoading && <Spinner />}
            {!this.state.isLoading && (
              <TableContainer>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell width={100}>Type</TableCell>
                      <TableCell>Provider(s)</TableCell>
                      <TableCell width={200}>Environment</TableCell>
                      <TableCell>Tester</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Created</TableCell>
                      <TableCell width={150} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {this.renderTableBody(this.state.result)}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Col>
        </Row>
        {this.renderPrivacyNotice()}
      </Container>
    );
  }
}
